import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { BigNumber } from "ethers";
import { motion } from "framer-motion";
import { rem } from "polished";
import React, { useEffect } from "react";
import { useCountUp } from "react-countup";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { selector, useRecoilValue, waitForAll } from "recoil";

import {
  accountAtom,
  accountSelector,
  activeAtom,
  ampBalanceAtom,
  ampPriceAtom,
  autoConnectTriedAtom,
  isInitializedSelector,
  suppliedBalanceSelector,
  totalRewardsSelector,
} from "../atoms";
import {
  FancyButton,
  Flex,
  H1,
  H3,
  SVG,
  Spacer,
  SubTitle,
  Tag,
  Text,
} from "../base";
import { formatEthAccount, formatNumber, formatUSD } from "../lib/format";
import { appSelector } from "../modules/apps";
import Flexacoin from "../modules/flexacoin";
import { useEntityTotals } from "../modules/hooks";
import i18n from "../modules/i18n";
import { AmpMark } from "./Icons";
import LogoIcon from "./LogoIcon";

const Container = styled(motion.div)`
  width: 100%;
  padding: 8.4rem 0;
  justify-content: left;
  align-items: center;
`;

const Subscript = styled(Text)`
  font-size: ${rem(30)};
  font-weight: 600;
`;

const useAutoCount = (end: any) => {
  const { countUp, update }: any = useCountUp({
    end,
  });
  useEffect(
    () => {
      if (typeof end === "number" || typeof end === "string") {
        update(end);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `update` isn't in a `useCallback` so it reruns each render
    [end]
  );

  return countUp;
};

enum Asset {
  USD = "usd",
  AMP = "amp",
}

type Numberish = BigNumber | number | string;

interface Figure {
  asset: Asset | string;
  value: Numberish;
}

type FigureTextProps = Figure & {
  size?: number;
  as?: Asset;
};

interface AssetMarkProps {
  asset: Asset | string;
  size?: number;
  color?: string;
}

const AssetMark = ({ asset, size, color }: AssetMarkProps) => {
  switch (asset) {
    case Asset.AMP:
      return (
        <AmpMark
          color={color}
          css={css`
            max-height: ${rem(size * 0.7)};
            width: ${rem(size * 0.8)};
            color: inherit;
          `}
        />
      );
    case Asset.USD:
      return <span>$</span>;
  }
  return null;
};

const FixedWidth = styled.span`
  font-variant-numeric: tabular-nums;
`;

const FigureText = ({ asset, value, as, size }: FigureTextProps) => {
  const { color } = useTheme();
  const price = useRecoilValue(ampPriceAtom);
  if (as) {
    let num = value;
    if (BigNumber.isBigNumber(value)) {
      num = value.toNumber();
    }
    return <FixedWidth>{formatUSD(Number(num) * price)}</FixedWidth>;
  }
  switch (asset) {
    case Asset.AMP: {
      return (
        <FixedWidth>
          {formatNumber(value)}
          <AssetMark asset={asset} color={color.mid} size={size} />
        </FixedWidth>
      );
    }
    case Asset.USD:
      return <FixedWidth>{formatUSD(value)}</FixedWidth>;
  }
};

const delaySelector = selector({
  key: "delaySelector",
  get: async () =>
    new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 2400);
    }),
});

export const billboardSelector = selector({
  key: "billboarState",
  get: ({ get }) => {
    get(
      waitForAll([isInitializedSelector, autoConnectTriedAtom, delaySelector])
    );

    const active = get(activeAtom);
    if (!active) {
      return {
        account: "",
        deployedBalance: BigNumber.from(0),
      };
    }

    const account = get(waitForAll([accountAtom]));
    if (!account) {
      return {
        account,
        deployedBalance: BigNumber.from(0),
      };
    }

    return {
      account,
      ampBalance: get(ampBalanceAtom),
      deployedBalance: get(suppliedBalanceSelector),
    };
  },
});

export const isLoadingSelector = selector({
  key: "isLoadingSelector",
  get: ({ get }) => get(waitForAll([billboardSelector])),
});

const parentVariants = {
  visible: { transition: { staggerChildren: 0.07 } },
  leaving: { transition: { staggerChildren: 0.07 } },
};

const childVariants = {
  entering: { x: 10, opacity: 0 },
  visible: { x: 0, opacity: 1 },
  leaving: { x: -10, opacity: 0 },
};

const BillBoard = ({
  title,
  subtitle = null,
  subscript = null,
  buttonProps = null,
  above = null,
}) => (
  <Container
    animate="visible"
    exit="leaving"
    initial="entering"
    variants={parentVariants}
  >
    {above ? <motion.div variants={childVariants}>{above}</motion.div> : null}
    <motion.div variants={childVariants}>
      <H1
        css={css`
          max-width: 90%;
          margin: 0;
        `}
      >
        {title}
      </H1>
    </motion.div>
    {subscript ? (
      <motion.div variants={childVariants}>
        <Spacer size={10} />
        <Subscript color="light">{subscript}</Subscript>
      </motion.div>
    ) : null}
    <Spacer size={20} />
    {subtitle && Array.isArray(subtitle) ? (
      subtitle.map((s, i) => (
        <motion.div key={`subtitle-${i}`} variants={childVariants}>
          <SubTitle>{s}</SubTitle>
          {i !== subtitle.length - 1 && <Spacer size={20} />}
        </motion.div>
      ))
    ) : (
      <motion.div variants={childVariants}>
        <SubTitle>{subtitle}</SubTitle>
      </motion.div>
    )}

    {buttonProps ? (
      <motion.div variants={childVariants}>
        <Spacer size={60} />
        <FancyButton gradient {...buttonProps} />
      </motion.div>
    ) : null}
  </Container>
);

export default BillBoard;

export const NetworkBillboard = ({ isConnected }) => {
  const [entity /* , totalsLoading, totalsError */] = useEntityTotals(
    "global",
    "amp"
  );

  const total = Flexacoin.toDecimal(entity?.totalSupply ?? BigNumber.from(0))
    .toNumber()
    .toFixed(0);

  const totalCount = useAutoCount(total);

  return (
    <BillBoard
      buttonProps={
        !isConnected
          ? {
              icon: SVG.ArrowIcon,
              title: i18n.t("buttons.connect"),
              to: "/connect",
              as: Link,
            }
          : {
              icon: SVG.ArrowIcon,
              title: i18n.t("buttons.startSupplying"),
              to: "/account",
              as: Link,
            }
      }
      subtitle={
        <Trans
          key="totalNetworkCapacity"
          components={{
            figure: (
              <FigureText asset={Asset.AMP} size={30} value={totalCount} />
            ),
          }}
          i18nKey="billboard.networkCapacity"
        />
      }
      title={<FigureText as={Asset.USD} asset={Asset.AMP} value={totalCount} />}
    />
  );
};

export const ActiveUserBillboard = () => {
  const totalSupply = useRecoilValue(suppliedBalanceSelector);
  const totalRewards = useRecoilValue(totalRewardsSelector);

  const deployed = Flexacoin.toDecimal(totalSupply).toNumber().toFixed(0);
  const rewards = Flexacoin.toDecimal(totalRewards).toNumber().toFixed(0);

  const deployedCount = useAutoCount(deployed);
  const rewardsCount = useAutoCount(rewards);
  // 'Welcome back'
  const title = i18n.t("billboard.welcomeBack");
  const subtitle = [
    <Trans
      key="supply"
      components={{
        figure: (
          <FigureText as={Asset.USD} asset={Asset.AMP} value={deployedCount} />
        ),
      }}
      i18nKey="billboard.userSupplying"
    />,
    <Trans
      key="earned"
      components={{
        figure: <FigureText asset={Asset.AMP} size={30} value={rewardsCount} />,
      }}
      i18nKey="billboard.userEarned"
    />,
  ];

  return (
    <BillBoard
      key="earned"
      buttonProps={{
        icon: SVG.ArrowIcon,
        title: i18n.t("buttons.startSupplying"),
        to: "/account",
        as: Link,
      }}
      subtitle={subtitle}
      title={title}
    />
  );
};

export const AddressBillboard = ({ address = "" }) => {
  const [entity] = useEntityTotals("accounts", address.substr(2));

  const totalSupplyTokens = Flexacoin.toDecimal(
    entity?.totalSupply ?? BigNumber.from(0)
  )
    .toNumber()
    .toFixed(0);
  const totalCount = useAutoCount(totalSupplyTokens);

  const totalRewardTokens = Flexacoin.toDecimal(
    entity?.totalReward ?? BigNumber.from(0)
  )
    .toNumber()
    .toFixed(0);

  const supplyCount = useAutoCount(totalSupplyTokens);
  const rewardCount = useAutoCount(totalRewardTokens);

  return (
    <BillBoard
      subscript={`at ${formatEthAccount(address)}`}
      subtitle={[
        <Trans
          key="supplying"
          components={{
            figure: (
              <FigureText asset={Asset.AMP} size={30} value={supplyCount} />
            ),
          }}
          i18nKey="billboard.addressSupplying"
        />,
        <Trans
          key="earned"
          components={{
            figure: (
              <FigureText asset={Asset.AMP} size={30} value={rewardCount} />
            ),
          }}
          i18nKey="billboard.addressEarned"
        />,
      ]}
      title={
        <FigureText
          as={Asset.USD}
          asset={Asset.AMP}
          value={totalCount?.toString() ?? "0"}
        />
      }
    />
  );
};

export const AppBillboard = ({ appId }) => {
  const app = useRecoilValue(appSelector(appId));

  const [entity] = useEntityTotals("apps", app.partition.substr(2));
  const totalSupplyTokens = Flexacoin.toDecimal(
    entity?.totalSupply ?? BigNumber.from(0)
  )
    .toNumber()
    .toFixed(0);

  const totalRewardTokens = Flexacoin.toDecimal(
    entity?.totalReward ?? BigNumber.from(0)
  )
    .toNumber()
    .toFixed(0);

  const totalCount = useAutoCount(totalSupplyTokens);
  const rewardCount = useAutoCount(totalRewardTokens);

  return (
    <BillBoard
      above={
        <>
          <Flex start>
            <LogoIcon
              css={css`width: ${rem(96)}; height: ${rem(96)}; }`}
              logo={app.logo}
            />
            <Spacer col size={28} />
            <Flex column align="flex-start">
              <H3>{app.name}</H3>
              <Spacer size={12} />
              <Flex>
                {app.poolType === "transformer" ? (
                  <Tag color={app.color}>Transformer</Tag>
                ) : (
                  <>
                    <Tag color={app.color}>iOS</Tag>
                    <Spacer col size={16} />
                    <Tag color={app.color}>Android</Tag>
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
          <Spacer size={48} />
        </>
      }
      buttonProps={{
        icon: SVG.ArrowIcon,
        title: i18n.t("buttons.supplyApp", {
          appName: app.name,
          poolType: app.poolType,
        }),
        to: "/account",
        as: Link,
      }}
      subtitle={[
        <Trans
          key="staked"
          components={{
            figure: (
              <FigureText asset={Asset.AMP} size={30} value={totalCount} />
            ),
          }}
          i18nKey="billboard.appSupplying"
          values={{
            appName: app.name,
          }}
        />,
        <span
          key="earned"
          css={css`
            font-size: ${rem(24)};
            line-height: 1.2;
          `}
        >
          <Trans
            components={{
              figure: (
                <FigureText asset={Asset.AMP} size={24} value={rewardCount} />
              ),
            }}
            i18nKey="billboard.appEarned"
            values={{
              appName: app.name,
            }}
          />
        </span>,
      ]}
      title={
        <FigureText
          as={Asset.USD}
          asset={Asset.AMP}
          value={totalCount?.toString() ?? "0"}
        />
      }
    />
  );
};

export const MainBillboard = () => {
  const { account, ampBalance, deployedBalance } =
    useRecoilValue(billboardSelector);
  const { isConnected } = useRecoilValue(accountSelector);

  const isActiveUser =
    account && isConnected && (ampBalance.gt(0) || deployedBalance.gt(0));
  return isActiveUser ? (
    <ActiveUserBillboard />
  ) : (
    <NetworkBillboard isConnected={account ? isConnected : null} />
  );
};

export const ExplorerBillboard = ({ slugType, slug }) =>
  slugType === "app" || slugType === "transformer" ? (
    <AppBillboard appId={slug} />
  ) : (
    <AddressBillboard address={slug} />
  );
